import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { parseISO, startOfDay } from 'date-fns';
import type { Timezone } from '@bb/domain/dist/types';
import { BBDateTimeFormat } from '@bb/domain/dist/types';
import { DATE_ONLY_REGEX } from 'utils/constants';
import type { AuthContextUser } from 'contexts/types';

const DEFAULT_TIMEZONE = 'America/Toronto' as Timezone;

export const createFormatDateFunction =
  (formatString: string) => (time: Date | string, timezone?: Timezone) => {
    if (!time) return '';

    // It doesn't make sense to zone a date without a time -> use UTC to maintain the same date, format and return.
    if (typeof time === 'string' && time.match(DATE_ONLY_REGEX)) {
      return format(parseISO(time), formatString);
    }

    const zonedTime = timezone
      ? utcToZonedTime(new Date(time), timezone)
      : new Date(time);
    return format(zonedTime, formatString);
  };

export const getLocalTimeZone = (auth?: AuthContextUser) =>
  // 1. device timezone
  (Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone) ||
  // 2. office timezone
  auth?.org?.addresses[Number(auth.location)][Number(auth.address)].timezone ||
  // 3. default timezone
  DEFAULT_TIMEZONE;

export const formatTimeStr = createFormatDateFunction(BBDateTimeFormat.Time);
export const formatTimeStrLowerCase = createFormatDateFunction(
  BBDateTimeFormat.TimeLowerCase,
);
export const formatDateStr = createFormatDateFunction(
  BBDateTimeFormat.DateWithDayOrd,
);
export const formatDateStrNoOrd = createFormatDateFunction(
  // @TODO: Add time format to domain
  BBDateTimeFormat.DateWithDayOrd.replace('do', 'd').replace('MMMM', 'MMM'),
);
export const formatDateStrWithYear = createFormatDateFunction(
  BBDateTimeFormat.DateWithYear,
);
export const formatDateStrWithYearAndDay = createFormatDateFunction(
  BBDateTimeFormat.DateWithYearAndDayOrd,
);
export const formatDateStrWithYearAndDayNoOrd = createFormatDateFunction(
  // @TODO: Add time format to domain
  BBDateTimeFormat.DateWithYearAndDayOrd.replace('do', 'd'),
);
export const formatDateStrWithYearAndFullDayNoOrd = createFormatDateFunction(
  BBDateTimeFormat.DateWithYearAndFullDayNoOrd,
);
export const formatDateStrWithYearShortMonthAndDayNoOrd =
  createFormatDateFunction(
    // @TODO: Add time format to domain
    BBDateTimeFormat.DateWithYearAndDayOrd.replace('do', 'd').replace(
      'MMMM',
      'MMM',
    ),
  );
export const formatDateStrWithTime = createFormatDateFunction(
  BBDateTimeFormat.DateWithTimeShortOrd,
);
export const formatDateStrWithYearShort = createFormatDateFunction(
  BBDateTimeFormat.DateWithYearShortOrd,
);
export const formatISODateStr = createFormatDateFunction(BBDateTimeFormat.ISO);
export const formatExactTimestamp = createFormatDateFunction(
  BBDateTimeFormat.ExactTimeStampShort,
);

export const getTimezoneCode = (timezone: Timezone, time?: Date | string) =>
  format(utcToZonedTime(time ? new Date(time) : new Date(), timezone), 'zzz', {
    timeZone: timezone,
  });

export const getStartOfDayInTimezone = (
  date: Date | string,
  timezone: Timezone,
) => zonedTimeToUtc(startOfDay(utcToZonedTime(date, timezone)), timezone);
